import React, { useState, useRef, useEffect } from 'react'
import { connect } from 'react-redux'
import Modal from '../../global/Modal'
import Translation from '../../global/Translation'
import { toggleHealthConditionsCheck, toggleSaleConditionsCheck } from '../../../store/checkout/actions'
import {
    getHealthConditionsCheck,
    getSaleConditionsCheck
} from '../../../store/checkout/reducers'
import Button from '../../global/Button'
import { FormattedHTMLMessage } from 'react-intl'
import Alerts from '../../alerts/Alerts'
import CheckboxLinkSaleConditions from './CheckboxLinkSaleConditions'
import CheckboxLinkHealthConditions from './CheckboxLinkHealthConditions'
import DepositMessage from '../../cut-to-size/deposit-message/DepositMessage'
import {requiredAdvancePayment} from '../../../store/login/reducers'

const mapStateToProps = state => {
    return {
        saleConditionsCheck: getSaleConditionsCheck(state),
        healthConditionsCheck: getHealthConditionsCheck(state),
        requiredAdvancePayment: requiredAdvancePayment(state)
    }
}

const mapDispatchToProps = dispatch => {
    return {
        toggleHealthConditionsCheck: checked => {
            dispatch(toggleHealthConditionsCheck(checked))
        },
        toggleSaleConditionsCheck: checked => {
            dispatch(toggleSaleConditionsCheck(checked))
        }
    }
}

const PreorderConditions = props => {
    const { onConfirm, onCancel, toggleHealthConditionsCheck, toggleSaleConditionsCheck, saleConditionsCheck, healthConditionsCheck, requiredAdvancePayment } = props

    const [isScrolledToBottom, setIsScrolledToBottom] = useState(false)
    const [scrollPercentage, setScrollPercentage] = useState(0.8)
    const modalContentRef = useRef(null)

     const updateScrollPercentage = () => {
        if (window.innerWidth <= 991) {
            setScrollPercentage(0.6)
        } else {
            setScrollPercentage(0.8)
        }
    }

    const handleScroll = () => {
        const content = modalContentRef.current
        const currentScrollPercentage = content.scrollTop / (content.scrollHeight - content.clientHeight)

        // If already set to true, do not change back to false
        if (currentScrollPercentage >= scrollPercentage && !isScrolledToBottom) {
            setIsScrolledToBottom(true)
        }
    }

    const unMarkChecks = () => {
        toggleSaleConditionsCheck(false)
        toggleHealthConditionsCheck(false)
    }

    useEffect(() => {
        updateScrollPercentage()
        window.addEventListener('resize', updateScrollPercentage)

        const content = modalContentRef.current
        if (content) {
            if (content.scrollHeight <= content.clientHeight) {
                setIsScrolledToBottom(true)
            }
            content.addEventListener('scroll', handleScroll)
        }

        // Cleanup
        return () => {
            if (content) {
                content.removeEventListener('scroll', handleScroll)
            }
            window.removeEventListener('resize', updateScrollPercentage)
        }
    }, [scrollPercentage])

    return (
        <div className="health-conditions-modal">
            <Modal
                title={
                    <React.Fragment>
                        <div>
                            <Translation id="special_orders_conditions" defaultMessage="Conditions of sale for special orders" />
                        </div>
                    </React.Fragment>
                }
                onClose={onCancel}
                showCloseButton={true}
                subtitle={
                    <div className="mt-3">
                        <Alerts
                            status="primary"
                            alert={'health_safety_information_scroll_down'}
                            cssIcon="fal fa-info-circle"
                            iconSize="18px"
                            className={'Phealth-conditions-modal-alert'}
                        />
                    </div>
                }
                footer={
                    <div className="preorder-conditions-checks">
                        <CheckboxLinkSaleConditions disabled={!isScrolledToBottom}/>
                        <CheckboxLinkHealthConditions disabled={!isScrolledToBottom}/>
                        {requiredAdvancePayment && (
                            <DepositMessage messageType={"INITIAL"}/>
                        )}
                        <div className="" style={{display:'flex', marginTop: '1rem', columnGap: '24px'}}>
                            <Button onClick={() => {
                                unMarkChecks()
                                onCancel()
                            }} style={{width:'100%'}}>
                                <Translation id="cancel" defaultMessage="Cancel" />
                            </Button>
                            <Button onClick={
                                () => {
                                    unMarkChecks()
                                    onConfirm()
                                }}
                                disabled={!saleConditionsCheck || !healthConditionsCheck}
                                inverted
                                style={{width:'100%'}}
                            >
                                <Translation id="approve_quotation_conditions" defaultMessage="Approve quotation" />
                            </Button>
                        </div>
                    </div>
                }
                ref={modalContentRef}
            >
                <p>
                    <FormattedHTMLMessage
                        id="preorder_conditions_message_content_1"
                        defaultMessage="All custom-made items are considered special orders. Manufactured Products also apply to Special Orders. No returns are accepted for Special Orders."
                    />
                </p>

                <p>
                    <FormattedHTMLMessage
                        id="preorder_conditions_message_content_2"
                        defaultMessage="All special orders are non-cancellable and will be subject to an advance payment equal to thirty per cent (30%) of the total amount of the order, which will be invoiced by Cosentino prior to order confirmation, including any legally applicable taxes."
                    />
                </p>
                <p>
                    <FormattedHTMLMessage
                        id="preorder_conditions_message_content_3"
                        defaultMessage="Cosentino-specific conditions"
                    />
                </p>
                <p>
                    <FormattedHTMLMessage
                        id="preorder_conditions_message_content_4"
                        defaultMessage="In the event that Cosentino agrees to supply Cosentino Products manufactured by Cosentino or Special Orders as defined above, the Customer acknowledges and agrees that Cosentino is not conducting any field testing or verification."
                    />
                </p>
                <p className="mb-0">
                    <FormattedHTMLMessage
                        id="preorder_conditions_message_content_5"
                        defaultMessage="Any workshop drawings prepared by Cosentino may be based on: (1) construction drawings, specifications and templates provided by the Customer and/or its design team; or (2) any construction drawings, specifications, templates and verified field measurements provided by the Customer or any other party and approved by the Customer and/or its design team."
                    />
                </p>
                <p>
                    <FormattedHTMLMessage
                        id="preorder_conditions_message_content_6"
                        defaultMessage="Final drawings are subject to verification and final approval by the Customer or its design team. Verification and approval of final drawings is the sole responsibility of the Customer, and the Customer shall rely on such verification and approval to process orders."
                    />
                </p>
                <p>
                    <FormattedHTMLMessage
                        id="preorder_conditions_message_content_7"
                        defaultMessage="Accordingly, Cosentino strongly recommends that a copy of the shop drawings be provided to the installer of the Cosentino Products and to other contractors involved in the installation for review and coordination during the approval process."
                    />
                </p>
                <p><b>
                    <FormattedHTMLMessage
                        id="preorder_conditions_message_content_8"
                        defaultMessage="COSENTINO SHALL NOT BE LIABLE FOR ANY FAULTS OR INACCURACIES IN THE COSENTINO PRODUCTS DUE TO ERRORS OR INACCURACIES IN THE DRAWINGS PROVIDED OR APPROVED BY CUSTOMER OR CUSTOMER'S DESIGN TEAM"
                    />
                </b></p>
                <p><b>
                    <FormattedHTMLMessage
                        id="preorder_conditions_message_content_9"
                        defaultMessage="LIKEWISE, COSENTINO SHALL NOT BE RESPONSIBLE FOR ANY VARIATIONS OR DISCREPANCIES BETWEEN THE SHOP DRAWINGS AND THE CONTRACT REQUIREMENTS. COSENTINO SHALL NOT ASSUME ANY ADDITIONAL COSTS OR EXPENSES ASSOCIATED WITH THE RECTIFICATION OF SUCH ERRORS, INACCURACIES, VARIATIONS OR DISCREPANCIES."
                    />
                </b></p>
                <p>
                    <FormattedHTMLMessage
                        id="preorder_conditions_message_content_10"
                        defaultMessage="In addition, to the extent that any alteration, addition, improvements, modification, or installation affects the operation of the Cosentino Products, every warranty provided by Cosentino, including without limitation, the implied warranty of merchantability, shall be deemed waived by the Customer and be null and void, and Cosentino shall have no further obligations to the Customer hereafter."
                    />
                </p>
            </Modal>
        </div>
    )
}

export default connect(mapStateToProps, mapDispatchToProps)(PreorderConditions)
